import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, AlertTriangle, Camera, FileText, Users, Clock, MapPin, Shield, ArrowRight, Building } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const EverettSlipFallPage = () => {
  const immediateSteps = [
    {
      title: "Seek medical attention right away",
      description: "Even if injuries seem minor—adrenaline masks pain, and delays hurt your claim."
    },
    {
      title: "Report the incident",
      description: "Tell the property owner/manager and get an incident report."
    },
    {
      title: "Take photos/videos",
      description: "Document the hazard, your injuries, scene, lighting, and any warning signs (or lack thereof)."
    },
    {
      title: "Get witness information",
      description: "Collect names and contact details from anyone who saw the fall."
    },
    {
      title: "Avoid recorded statements",
      description: "Don't give recorded statements to insurance adjusters without a lawyer."
    },
    {
      title: "Contact me ASAP",
      description: "Washington's 3-year statute of limitations starts on the injury date (RCW 4.16.080). Evidence disappears fast."
    }
  ];

  const commonCauses = [
    "Wet floors from leaks, spills, or tracked-in rain (grocery stores, restaurants)",
    "Uneven sidewalks or parking lot potholes/cracks",
    "Icy stairs/ramps in winter",
    "Poor lighting in hallways, stairwells, or lots",
    "Loose mats, debris, or faulty handrails in apartments/retail"
  ];

  const compensationTypes = [
    "Medical bills (past and future)",
    "Lost wages",
    "Pain and suffering",
    "Rehabilitation costs",
    "Reduced quality of life"
  ];

  const faqs = [
    {
      question: "How much is my slip and fall case worth?",
      answer: "It depends on your injuries, but I'll give you honest ranges during our consultation—no cookie-cutter answers."
    },
    {
      question: "What if the property denies responsibility?",
      answer: "We gather evidence to prove negligence—photos, witness statements, maintenance records, and more."
    },
    {
      question: "Do I need a lawyer for a 'minor' fall?",
      answer: "Yes—insurers lowball unrepresented victims. Studies show represented claimants get 3.5x more on average."
    }
  ];

  // FAQ data for schema
  const faqSchemaData = [
    {
      question: "How much is my Everett slip and fall case worth?",
      answer: "The value depends on injury severity, medical costs, lost wages, and pain/suffering. Amy provides honest case evaluations during free consultations—no cookie-cutter answers."
    },
    {
      question: "What if the property owner denies responsibility for my fall?",
      answer: "Amy gathers evidence to prove negligence including photos, witness statements, maintenance records, and surveillance footage requests."
    },
    {
      question: "Do I need a lawyer for a minor slip and fall?",
      answer: "Yes. Insurance companies lowball unrepresented victims. Studies show represented claimants receive 3.5x more on average, even after attorney fees."
    },
    {
      question: "What is the statute of limitations for slip and fall in Washington?",
      answer: "Washington has a 3-year statute of limitations for personal injury claims (RCW 4.16.080), starting from the date of injury. Contact an attorney promptly to preserve evidence."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Everett Slip and Fall Lawyer | Premises Injury Attorney | Amy C. Brown"
        description="Injured in a slip and fall in Everett, WA? Amy C. Brown fights for maximum compensation on contingency. Former defense attorney, no fees unless you win. Call (425) 675-1224."
        faqData={faqSchemaData}
        localArea={{ city: "Everett", county: "Snohomish", state: "Washington" }}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-secondary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4 text-teal-400" />
              <span className="text-white/90 text-sm font-medium">Serving Everett & Snohomish County</span>
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Everett Slip and Fall Lawyer – Get the Compensation You Deserve After a Premises Injury
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-6">
              If you've been hurt in a slip and fall accident in Everett, Washington, you're not alone—and you don't have to handle the insurance companies by yourself.
            </p>

            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              Everett sees frequent slip and falls due to rainy weather, retail stores like Alderwood Mall, grocery aisles, parking lots, sidewalks, and workplaces near the port or Boeing areas. Wet floors, uneven pavement, icy steps in winter, or unmarked spills cause serious injuries: broken bones, head trauma, back/spinal damage, and long-term pain.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
              <p className="text-white leading-relaxed">
                As a <strong>former defense attorney</strong> now fighting exclusively for injured people like you, I know exactly how insurance companies try to minimize or deny claims. I use that insider knowledge to build strong cases and get maximum recovery—on a <strong>no-win, no-fee contingency basis</strong>. You pay nothing unless we win.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all border border-white/20"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Immediate Steps Section */}
      <section className="py-20 bg-white" data-testid="steps-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              What Should You Do Immediately After a Slip and Fall in Everett?
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Taking the right steps early protects your health AND your legal claim.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {immediateSteps.map((step, index) => (
                <div key={index} className="flex gap-4 bg-slate-50 rounded-xl p-6">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary mb-1">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes Section */}
      <section className="py-20 bg-slate-50" data-testid="causes-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Common Causes of Slip and Falls in Everett
              </h2>
              <ul className="space-y-4">
                {commonCauses.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-4">
                Who Pays for My Injuries?
              </h3>
              <p className="text-slate-600 mb-4 font-medium">Premises Liability in Washington</p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Property owners (stores, landlords, businesses) must keep premises reasonably safe for visitors. If negligence caused your fall—failure to clean, repair, or warn—you can seek compensation for:
              </p>
              <ul className="space-y-3 mb-6">
                {compensationTypes.map((type, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-slate-700">{type}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <p className="text-sm text-slate-600">
                  <strong>Washington uses pure comparative negligence</strong>—if you're partially at fault (e.g., not watching your step), your award reduces proportionally, but you can still recover.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="py-20 bg-white" data-testid="why-me-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              Why Choose a Solo Attorney Like Me Over Big Firms?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="bg-slate-50 rounded-xl p-6">
                <Users className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Direct Access</h3>
                <p className="text-slate-600">No paralegal hand-offs. You work with me personally throughout your case.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Shield className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Personalized Attention</h3>
                <p className="text-slate-600">From day one, your case gets my full focus—not lost in a big firm shuffle.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Building className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Defense Background</h3>
                <p className="text-slate-600">I anticipate and counter insurance company tactics because I used to work for them.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <CheckCircle className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Proven Results</h3>
                <p className="text-slate-600">Six-figure settlements for injury clients. See testimonials on my homepage.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-12 text-center">
              Frequently Asked Questions About Everett Slip and Falls
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-secondary mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Worried About Costs? Zero Upfront Fees.
            </h2>
            <p className="text-xl text-slate-300 mb-4">
              Contingency only. Call or text me directly for a free, no-obligation review of your Everett slip and fall case.
            </p>
            <p className="text-lg text-slate-300 mb-8">
              Let's talk—I'm here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold transition-all"
                data-testid="cta-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/faqs" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              See Full FAQs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Read Related Blog Posts <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/is-amy-right-for-you" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Is Amy Right For You? <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EverettSlipFallPage;
